import { useEffect, useRef } from 'react';

/*
 * Botão "voltar" (Android, navegador) fecha o painel/modal aberto em vez de sair da página.
 * Ao abrir, cria uma entrada no histórico (mesma URL); o voltar consome essa entrada e fecha o painel.
 * Fechar pelo X/Cancelar remove a entrada. Com vários painéis abertos, fecha só o de cima.
 * Se algo foi digitado no painel, pede confirmação antes de descartar.
 */
const DISCARD_MSG = 'Descartar o que foi digitado?';
const stack = []; // painéis abertos, o último é o de cima
let ownPops = 0; // popstates causados por history.back() deste hook
let pendingBack = false; // um painel fechou neste render; se outro abrir no mesmo render, reaproveita a entrada
let nextId = 1;

const isOurEntry = () => Boolean(window.history.state?.emPanel);
const pushEntry = (entry, replace) => {
  const state = { ...window.history.state, emPanel: entry.id };
  if (replace) window.history.replaceState(state, '');
  else window.history.pushState(state, '');
};

function onPopState() {
  if (ownPops > 0) { ownPops -= 1; return; }
  const top = stack[stack.length - 1];
  if (!top) return;
  if (top.dirty && !window.confirm(DISCARD_MSG)) {
    pushEntry(top); // continua aberto: recoloca a entrada que o voltar consumiu
    return;
  }
  stack.pop();
  top.closedByBack = true;
  top.onClose.current?.();
}

// Qualquer digitação (inclusive em listas/menus em portal e upload de arquivo) marca o painel de cima
function onInput() {
  const top = stack[stack.length - 1];
  if (top) top.dirty = true;
}

window.addEventListener('popstate', onPopState);
document.addEventListener('input', onInput, true);

export function useBackClose(open, onClose) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const entry = { id: nextId++, onClose: onCloseRef, dirty: false, closedByBack: false };
    stack.push(entry);
    if (pendingBack && isOurEntry()) {
      pendingBack = false;
      pushEntry(entry, true);
    } else {
      pushEntry(entry);
    }

    return () => {
      const idx = stack.indexOf(entry);
      if (idx >= 0) stack.splice(idx, 1);
      if (!entry.closedByBack && isOurEntry()) {
        pendingBack = true;
        // Depois do render: se nenhum painel reaproveitou a entrada, remove ela do histórico
        queueMicrotask(() => {
          if (!pendingBack) return;
          pendingBack = false;
          if (!isOurEntry()) return;
          ownPops += 1;
          window.history.back();
        });
      }
    };
  }, [open]);
}
